import { Injectable } from '@angular/core';
import {
  GetAllGoodsFilterParams,
  GetAllGoodsParams,
  GetAllGoodsSortParams,
  V3InspectionDataSourceService
} from '../data-sources/v3/inspection/inspection-data-source.v3.service';
import { TaskDataSourceService } from '../data-sources/task-data-source.service';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { V3CrateCommentsDataSourceService } from '../data-sources/v3/crates-comments/crate-comments-data-source.service';
import { CrateComments } from 'src/models/CrateComments';
import { Inspection } from '../data-sources/v3/inspection/inspection.type';

const PAGE_SIZE_OPTIONS = [30, 60, 90];

@Injectable({
  providedIn: 'root'
})
export class InspectionService {
  crateCommentsQty$: BehaviorSubject<CrateComments[]> = new BehaviorSubject([]);
  private $pageSize: BehaviorSubject<number> = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems: Subject<number> = new Subject<number>();
  private $pageCount: Subject<number> = new Subject<number>();
  public $currentPage: Subject<number> = new Subject<number>();
  private $atLastPage: Subject<boolean> = new Subject<boolean>();
  private $atFirstPage: Subject<boolean> = new Subject<boolean>();
  private $items: Subject<any[]> = new Subject<any[]>();
  private $pageSizeOptions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private pageSize = PAGE_SIZE_OPTIONS[0];
  private pageSizeOptions = PAGE_SIZE_OPTIONS;
  private pageCount;
  public currentPage;
  private totalItems;

  constructor(
    private v3InspectionDataSource: V3InspectionDataSourceService,
    private taskDataSourceService: TaskDataSourceService,
    private crateCommentsDataSource: V3CrateCommentsDataSourceService
  ) { 
    this.$pageSize.next(this.pageSize);
    this.$pageSizeOptions.next(this.pageSizeOptions);
  }

  async init() {
    return this.getPage(1);
  }

  async reload(filterParams: GetAllGoodsFilterParams = null, sortParams: GetAllGoodsSortParams = null) {
    return await this.getPage(this.currentPage, filterParams, sortParams);
  }

  async getNextPage(filterParams: GetAllGoodsFilterParams = null, sortParams: GetAllGoodsSortParams = null) {
    if (this.currentPage < this.pageCount) {
      return await this.getPage(this.currentPage + 1, filterParams, sortParams);
    } else {
      throw Error('???');
    }
  }

  async getPreviousPage(filterParams: GetAllGoodsFilterParams = null, sortParams: GetAllGoodsSortParams = null) {
    if (this.currentPage > 1) {
      return await this.getPage(this.currentPage - 1, filterParams, sortParams);
    } else {
      throw Error('???');
    }
  }

  private async getPage(page: number, filterParams: GetAllGoodsFilterParams = null, sortParams: GetAllGoodsSortParams = null) {
    this.currentPage = page;
    const params: GetAllGoodsParams = {
      limit: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(params, filterParams);
    Object.assign(params, sortParams);

    const tasksPromise = this.taskDataSourceService.getAllTasks().then(tasks => {
      const taskMap = new Map<string, Task>();
      for (const task of tasks) {
        taskMap.set(task.tagRfid, task);
      }
      return taskMap;
    });

    const goodsPromise = this.v3InspectionDataSource.getAll(params);
    const [response, taskMap] = await Promise.all([goodsPromise, tasksPromise]);

    //TODO: not call tasks so much.
    const items = [];
    for (const item of response.values) {
      item.inspectionState = item.state;
      const task: any = taskMap.get(item.tagRfid);
      if (task) {
        switch (task.status) {
          case "IN_PROCESS":
            item.inspectionState = task.status;
            break;
          case "QUEUE":
            break;
          default:
            break;
        }
      }
      items.push(item);
    }
    this.totalItems = response.count;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(items);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
    this.getCrateCommentsQty(response.values);
  }

  async setPageSize(pageSize: number){
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async setPage(page: number, filter: GetAllGoodsFilterParams = null, sortParams: GetAllGoodsSortParams){
    this.currentPage = page;
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  getCrateCommentsQty(values: Inspection[]) {
    if (values.length) {
      const cratesIds = getCratesIds(values);
      this.crateCommentsDataSource.getAll(cratesIds).subscribe(comments => {
        this.crateCommentsQty$.next(comments);
      });
    }
  }

  async getTotalProductsCount(filterParams: GetAllGoodsFilterParams = null) {
    return await this.v3InspectionDataSource.getInspectionProducts(filterParams);
  }

  get currentPage$(): Observable<number> {
    return this.$currentPage;
  }

  get atFirstPage$(): Observable<boolean> {
    return this.$atFirstPage;
  }

  get atLastPage$(): Observable<boolean> {
    return this.$atLastPage;
  }

  get totalItems$(): Observable<number> {
    return this.$totalItems;
  }

  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }

  get items$(): Observable<any[]> {
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }
}

function getCratesIds(values: Inspection[]) {
  return values.map(crate => crate.id);
}